import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Zap, Headphones, TrendingUp, PenLine, Code2, BarChart3, BookOpen, Sparkles, Wallet,
  type LucideIcon,
} from 'lucide-react';
import Button from '../components/ui/Button';
import ProductCard from '../components/product/ProductCard';
import { AI_AGENT_USE_CASES, SUBSCRIPTION_PLANS } from '../data/mockData';
import { useProducts } from '../hooks/useProducts';
import { usePageMeta } from '../hooks/usePageMeta';
import styles from './HomePage.module.css';

// Each use-case tile gets its own icon + color instead of a repeated generic
// glyph, so the grid actually reads as 9 different categories at a glance.
const USE_CASE_STYLE: Record<string, { icon: LucideIcon; color: string }> = {
  'Productivity':         { icon: Zap,         color: '#D9A857' },
  'Customer Support':     { icon: Headphones,  color: '#06B6D4' },
  'Sales & Marketing':    { icon: TrendingUp,  color: '#7BC48F' },
  'Content & Writing':    { icon: PenLine,     color: '#EC4899' },
  'Coding & Development': { icon: Code2,       color: '#8A5CF6' },
  'Data & Analytics':     { icon: BarChart3,   color: '#4F46E5' },
  'Research & Knowledge': { icon: BookOpen,    color: '#F0665C' },
  'Personal Assistant':   { icon: Sparkles,    color: '#F59E0B' },
  'Finance & Accounting': { icon: Wallet,      color: '#22C55E' },
};

const USE_CASE_DESC: Record<string, string> = {
  'Productivity':         'Automate busywork and reclaim your day',
  'Customer Support':     'Resolve tickets and chats around the clock',
  'Sales & Marketing':    'Qualify leads and write campaigns that convert',
  'Content & Writing':    'Draft, edit, and publish faster',
  'Coding & Development': 'Ship code, review PRs, squash bugs',
  'Data & Analytics':     'Turn raw data into real answers',
  'Research & Knowledge': 'Summarize, search, and cite sources',
  'Personal Assistant':   'Manage your calendar, inbox, and to-dos',
  'Finance & Accounting': 'Reconcile books and forecast cash flow',
};

// Nothing is listed on the marketplace yet — hide the sections that only make
// sense once there's real product data, without deleting them. Flip to true
// once there are enough real listings to fill these out.
const SHOW_LISTING_SECTIONS = false;

export default function HomePage() {
  const { data: allProducts = [] } = useProducts({ sort: 'sales_count', limit: 12 });
  const { data: aiProducts  = [] } = useProducts({ category: 'ai_agents', sort: 'sales_count', limit: 4 });
  const eliteProducts = allProducts;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollIndex, setScrollIndex] = useState(0);

  usePageMeta({
    description: 'Aiphra is the AI agent marketplace built for performance. Discover autonomous AI agents, or list your own with low fees and fast payouts.',
  });

  const scroll = (dir: -1 | 1) => {
    const next = Math.max(0, Math.min(scrollIndex + dir, eliteProducts.length - 1));
    setScrollIndex(next);
    scrollRef.current?.scrollTo({ left: next * 300, behavior: 'smooth' });
  };

  return (
    <div className={styles.page}>
      {/* ─── HERO ─── */}
      <section className={styles.hero}>
        <div className={styles.gridLines} />
        <div className={styles.heroBg} />

        <div className={styles.heroInner}>
          {/* Left column */}
          <div className={styles.heroContent}>
            <h1 className={[styles.heroHeadline, styles.fadeInUp].join(' ')} style={{ animationDelay: '0.25s' }}>
              <span className={styles.heroLine1}>Sell AI Agents.</span>
              <span className={styles.heroLine2}>
                Keep <span className={styles.heroGold}>what you earn.</span>
              </span>
            </h1>

            <div className={[styles.heroCtas, styles.fadeInUp].join(' ')} style={{ animationDelay: '0.55s' }}>
              <Link to="/signup" className={styles.ctaPrimary}>
                Start Selling Free <span className={styles.arrow}>→</span>
              </Link>
              <Link to="/explore" className={styles.ctaSecondary}>
                Browse Products <span className={styles.arrow}>→</span>
              </Link>
            </div>

            <div className={[styles.heroStats, styles.fadeInUp].join(' ')} style={{ animationDelay: '0.7s' }}>
              <div className={styles.stat}>
                <span className={styles.statNum}>5%</span>
                <span className={styles.statLabel}>Platform fee (Pro)</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statNum}>1 day</span>
                <span className={styles.statLabel}>Fastest payout (Elite)</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statNum}>16+</span>
                <span className={styles.statLabel}>Agent use cases</span>
              </div>
            </div>
          </div>

          {/* Right column — a real listing, not a decorative mascot */}
          <div className={[styles.heroVisual, styles.fadeInUp].join(' ')} style={{ animationDelay: '0.4s' }}>
            <HeroListingCard product={allProducts[0]} />
          </div>
        </div>

        <div className={styles.scrollIndicator}>
          <div className={styles.scrollLine}>
            <div className={styles.scrollDot} />
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS — focused on what's unique ─── */}
      <section className={styles.section}>
        <div className="container">
          <div className={styles.sectionHeader}>
            <span className={styles.sectionComment}>How it works</span>
            <h2 className={styles.sectionTitle}>Different by design.</h2>
            <p className={styles.sectionSub}>
              Every other marketplace just takes a cut and leaves you to it.
              Aiphra actively rewards you for growing.
            </p>
          </div>

          <div className={styles.stepsGrid}>
            {[
              {
                step: '01',
                icon: <UploadIcon />,
                title: 'List free. No commitment.',
                body: 'Upload your tool, set your price, go live. Free plan charges 7% — no monthly fee, no gatekeeping. List your first 3 products without spending a cent.',
              },
              {
                step: '02',
                icon: <UsersIcon />,
                title: 'Get paid fast.',
                body: 'No 30-day holds. Payouts land in 1-7 business days depending on your plan, straight to your wallet.',
              },
              {
                step: '03',
                icon: <TrendIcon />,
                title: 'Lower fees as you grow.',
                body: 'Free is 7%. Pro is 5%. Elite is 4%. As your revenue grows, your fees drop and your buyer discounts increase — creating a flywheel that rewards your best sellers.',
              },
            ].map((step, i) => (
              <div key={i} className={styles.stepCard}>
                <span className={styles.stepNum}>{step.step}</span>
                <div className={styles.stepIcon}>{step.icon}</div>
                <h3 className={styles.stepTitle}>{step.title}</h3>
                <p className={styles.stepBody}>{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── AI SHOWCASE ───
          Hidden until there's real listing data to show — re-enable by
          flipping SHOW_LISTING_SECTIONS to true up top. */}
      {SHOW_LISTING_SECTIONS && (
      <section className={styles.aiShowcase}>
        <div className="container">
          <div className={styles.aiShowcaseHeader}>
            <div>
              <div className={styles.aiEyebrow}>
                <span className={styles.aiEyebrowDot}>✦</span>
                <span>AI Showcase</span>
                <span className={styles.aiEyebrowBadge}>Built for the AI era</span>
              </div>
              <h2 className={styles.aiShowcaseTitle}>AI agents & tools, curated.</h2>
              <p className={styles.aiShowcaseSub}>
                The AI agent market hit $10.8B in 2026. These are the tools builders actually use.
              </p>
            </div>
            <Link to="/explore" className={styles.aiSeeAll}>
              View all AI agents →
            </Link>
          </div>

          {aiProducts.length > 0 ? (
            <div className={styles.aiGrid}>
              {aiProducts.map(p => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          ) : (
            <div className={styles.aiEmpty}>
              <p>New listings are added regularly — check back soon, or be the first.</p>
              <Link to="/signup" className={styles.aiSeeAll}>List your agent →</Link>
            </div>
          )}
        </div>
      </section>
      )}

      {/* ─── ELITE PICKS ───
          Hidden until there's real listing data to show — re-enable by
          flipping SHOW_LISTING_SECTIONS to true up top. */}
      {SHOW_LISTING_SECTIONS && (
      <section className={styles.section}>
        <div className="container">
          <div className={styles.sectionHeader}>
            <span className={styles.sectionComment}>Featured products</span>
            <p className={styles.sectionSub}>Top performing products this week</p>
          </div>

          {eliteProducts.length > 0 ? (
            <div className={styles.carouselWrap}>
              <button className={[styles.carouselBtn, styles.carouselLeft].join(' ')} onClick={() => scroll(-1)} aria-label="Previous">‹</button>
              <div className={styles.carouselTrack} ref={scrollRef}>
                {eliteProducts.map(p => (
                  <ProductCard key={p.id} product={p} variant="hero" />
                ))}
              </div>
              <button className={[styles.carouselBtn, styles.carouselRight].join(' ')} onClick={() => scroll(1)} aria-label="Next">›</button>
            </div>
          ) : (
            <div className={styles.aiEmpty}>
              <p>No products yet — this is where top performers will show up.</p>
            </div>
          )}

          <div className={styles.viewAll}>
            <Link to="/explore" className={styles.viewAllLink}>
              View all Elite products →
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* ─── USE CASES ─── */}
      <section className={styles.section}>
        <div className="container">
          <div className={styles.sectionHeader}>
            <span className={styles.sectionComment}>Browse by use case</span>
            <p className={styles.sectionSub}>Every listing on Aiphra is an AI agent — browse by what you need it to do.</p>
          </div>

          <div className={styles.useCaseGrid}>
            {AI_AGENT_USE_CASES.slice(0, 9).map(({ label, tag }, i) => {
              const { icon: Icon, color } = USE_CASE_STYLE[label] ?? { icon: Sparkles, color: '#8A5CF6' };
              const desc = USE_CASE_DESC[label] ?? 'Explore agents built for this.';
              const isHero = i === 0;
              return (
                <Link
                  key={tag}
                  to={`/explore?useCase=${encodeURIComponent(tag)}`}
                  className={[styles.useCaseCard, isHero ? styles.useCaseCardHero : ''].join(' ')}
                  style={{ '--cat-color': color } as React.CSSProperties}
                >
                  <span className={styles.useCaseMesh} aria-hidden="true" />
                  <span className={styles.useCaseOrb} aria-hidden="true">
                    <span className={styles.useCaseOrbRing} />
                    <Icon size={isHero ? 28 : 19} strokeWidth={1.75} />
                  </span>
                  <span className={styles.useCaseBody}>
                    <span className={styles.useCaseName}>{label}</span>
                    <span className={styles.useCaseDesc}>{desc}</span>
                  </span>
                  <span className={styles.useCaseArrow} aria-hidden="true">→</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── VALUE PROP ─── */}
      <section className={styles.valueProp}>
        <div className="container">
          <div className={styles.valuePropInner}>
            <div className={styles.valuePropText}>
              <span className={styles.sectionComment}>Seller economics</span>
              <h2 className={styles.valuePropTitle}>Lower fees, higher take-home.</h2>
              <p className={styles.valuePropBody}>
                Every paid plan drops your platform fee and speeds up your payouts. The more you
                grow, the less Aiphra takes and the faster you get paid.
              </p>

              <div className={styles.tierRows}>
                {SUBSCRIPTION_PLANS.filter(p => p.price_monthly > 0).map(plan => (
                  <div key={plan.id} className={styles.tierRow}
                    style={{ '--tier-accent': `var(--tier-${plan.name.toLowerCase()})` } as React.CSSProperties}>
                    <span className={styles.tierRowBar} />
                    <span className={styles.tierRowName}>{plan.name}</span>
                    <span className={styles.tierRowPrice}>${plan.price_monthly}/mo</span>
                    <span className={styles.tierRowFee}>{plan.platform_fee_pct}% fee</span>
                    <span className={styles.tierRowNet} style={{ color: 'var(--accent-success)', fontSize: 12 }}>
                      {plan.payout_speed_days === 1 ? 'next-day payouts' : `${plan.payout_speed_days}-day payouts`}
                    </span>
                  </div>
                ))}
              </div>

              <Link to="/pricing">
                <Button variant="primary" size="md">See all plans →</Button>
              </Link>
            </div>

            <div className={styles.valuePropVisual}>
              <FeeTiersCard />
            </div>
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─── */}
      <section className={styles.finalCta}>
        <div className={styles.finalCtaGlow} />
        <div className="container" style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <h2 className={styles.finalCtaTitle}>Your first sale is one listing away.</h2>
          <p className={styles.finalCtaSub}>
            Free to join. First product live in 10 minutes.
          </p>
          <div className={styles.finalCtaBtns}>
            <Link to="/signup">
              <Button variant="primary" size="lg">Start Selling Free</Button>
            </Link>
            <Link to="/explore" className={styles.browseCta}>
              Browse Products <span className={styles.arrow}>→</span>
            </Link>
          </div>
          <div className={styles.finalCtaTrust}>
            <span>✓ No credit card required</span>
            <span>✓ Fees as low as 4%</span>
            <span>✓ Payouts in as fast as 1 day</span>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ─── Sub-components ─── */
function FeeTiersCard() {
  const paidPlans = SUBSCRIPTION_PLANS.filter(p => p.price_monthly > 0);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setIdx(i => (i + 1) % paidPlans.length), 1800);
    return () => clearInterval(id);
  }, [paidPlans.length]);

  const plan = paidPlans[idx];

  return (
    <div className={styles.feeCard}>
      <div className={styles.feeCardHeader}>
        <span className={styles.feeCardLabel}>Aiphra Fee</span>
        <span className={styles.feeCardTier}>{plan.name}</span>
      </div>
      <div className={styles.feeAmount}>
        <span className={styles.feeNum}>{plan.platform_fee_pct}%</span>
      </div>
      <div className={styles.feeProgress}>
        <div className={styles.feeProgressFill} style={{ width: `${40 + idx * 30}%` }} />
      </div>
      <span className={styles.feeProgressLabel}>
        Payout in {plan.payout_speed_days} day{plan.payout_speed_days > 1 ? 's' : ''}
      </span>
    </div>
  );
}

// ── Hero visual, no real listings yet: an animated preview, clearly labeled
// as a preview — not a fabricated product pretending to be a real one. ──
const DEMO_AGENTS = [
  { title: 'Customer Support Agent', tagline: 'Answers tickets while you sleep.' },
  { title: 'Content Repurposing Agent', tagline: 'Turns one post into ten, automatically.' },
  { title: 'Code Review Agent', tagline: 'Catches bugs before your team does.' },
] as const;

const DEMO_STEPS = ['Listing published', 'A buyer finds it', 'Delivered instantly'] as const;

function HeroDemoCard() {
  const [demoIdx, setDemoIdx] = useState(0);
  const [stepIdx, setStepIdx] = useState(0);
  const [reduceMotion, setReduceMotion] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduceMotion(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (reduceMotion) return;
    const id = setInterval(() => {
      setStepIdx(s => {
        const next = (s + 1) % DEMO_STEPS.length;
        if (next === 0) setDemoIdx(d => (d + 1) % DEMO_AGENTS.length);
        return next;
      });
    }, 1800);
    return () => clearInterval(id);
  }, [reduceMotion]);

  const demo = DEMO_AGENTS[demoIdx];

  return (
    <div className={styles.listingCard}>
      <div className={styles.listingTop}>
        <div className={styles.previewBadge}>Preview</div>
        <div className={styles.agentStatus}>
          <span className={styles.agentStatusDot} />
          What a listing looks like
        </div>
      </div>

      <div className={styles.listingBody}>
        <h3 className={[styles.listingTitle, styles.demoFade].join(' ')} key={`t-${demoIdx}`}>{demo.title}</h3>
        <p className={[styles.listingTagline, styles.demoFade].join(' ')} key={`s-${demoIdx}`}>{demo.tagline}</p>

        <div className={styles.demoSteps}>
          {DEMO_STEPS.map((step, i) => (
            <div key={step} className={[styles.demoStep, i <= stepIdx || reduceMotion ? styles.demoStepDone : ''].join(' ')}>
              <span className={styles.demoStepDot} />
              {step}
            </div>
          ))}
        </div>

        <div className={styles.listingEmpty}>
          <p>Your first real listing will show up here.</p>
          <Link to="/signup" className={styles.ctaSecondary}>List an agent →</Link>
        </div>
      </div>
    </div>
  );
}

// ── Hero visual: a real listing card, not a decorative mascot ──
type HeroProduct = {
  id: string;
  title: string;
  tagline?: string | null;
  price: number;
  is_free?: boolean;
  version?: string | null;
  rating_avg?: number;
  rating_count?: number;
  sales_count?: number;
  seller?: { display_name?: string | null; username?: string | null } | null;
};

function HeroListingCard({ product }: { product?: HeroProduct }) {
  if (!product) {
    return <HeroDemoCard />;
  }

  const initials = (product.seller?.display_name || product.seller?.username || 'AI')
    .split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className={styles.listingCard}>
      <div className={styles.listingTop}>
        <div className={styles.listingSeller}>
          <div className={styles.sellerAvatar}>{initials}</div>
          <div>
            <div className={styles.sellerName}>{product.seller?.display_name ?? 'Aiphra seller'}</div>
            {product.seller?.username && <div className={styles.sellerHandle}>@{product.seller.username}</div>}
          </div>
        </div>
        <div className={styles.agentStatus}>
          <span className={styles.agentStatusDot} />
          Agent online
        </div>
      </div>

      <div className={styles.listingBody}>
        <h3 className={styles.listingTitle}>{product.title}</h3>
        {product.tagline && <p className={styles.listingTagline}>{product.tagline}</p>}

        <div className={styles.listingMeta}>
          {typeof product.rating_avg === 'number' && product.rating_count ? (
            <>
              <span className={styles.stars}>★★★★★</span>
              <span>{product.rating_avg.toFixed(1)}</span>
              <span className={styles.metaDot} />
            </>
          ) : null}
          {product.version && <span>v{product.version}</span>}
        </div>

        <div className={styles.listingFooter}>
          <div className={styles.priceTag}>
            {product.is_free ? 'Free' : `$${product.price}`}
          </div>
          <Link to="/explore" className={styles.ctaPrimary}>Get it now</Link>
        </div>
      </div>
    </div>
  );
}

function TrendIcon() {
  return <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>;
}

function UploadIcon() {
  return <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M16 8l-4-4-4 4M12 4v12"/></svg>;
}
function UsersIcon() {
  return <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>;
}
